import { settingsWords as words, settingsText } from "./settings-copy";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Blocker,
  Box,
  Conflict,
  TaskBox,
} from "@/components/ui/box.schema";
import { useTabDraft } from "@/components/ui/box-draft";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { currentOnConflict } from "@/lib/api/conflict";
import { gradeScaleApi, type GradeScale } from "@/lib/api/gradeScale";
import {
  boxRead,
  imam,
  useSettingsCommit,
  type PendingSettingsSave,
} from "@/components/settings/settings-boxes";
import { settingsCopy as copy } from "@/components/settings/settings-copy";

type Band = { letter: string; min_score: string; gpa_points: string };
type Issue = { index: number; field: keyof Band; sentence: string };
function bandIssues(bands: Band[]): Issue[] {
  return bands.flatMap((band, index) => {
    const issues: Issue[] = [];
    const add = (field: keyof Band, sentence: string) =>
      issues.push({ index, field, sentence });
    if (
      !band.letter.trim() ||
      bands.some(
        (other, i) => i !== index && other.letter.trim() === band.letter.trim(),
      )
    )
      add("letter", settingsText.uniqueBand(index + 1));
    const min = Number(band.min_score),
      gpa = Number(band.gpa_points);
    if (
      !band.min_score.trim() ||
      !Number.isInteger(min) ||
      min < 0 ||
      min > 100
    )
      add("min_score", settingsText.bandMinimum(band.letter || index + 1));
    if (!band.gpa_points.trim() || !Number.isFinite(gpa) || gpa < 0 || gpa > 4)
      add("gpa_points", settingsText.bandGpa(band.letter || index + 1));
    return issues;
  });
}
const bandDraft = (scale: GradeScale): Band[] =>
  scale.bands.map((band) => ({
    letter: band.letter,
    min_score: String(band.min_score),
    gpa_points: String(band.gpa_points),
  }));

export function useGradeScaleBox(): Box {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["grade-scale"],
    queryFn: gradeScaleApi.getGradeScale,
    retry: false,
  });
  const [serverTheirs, setTheirs] = useState<GradeScale | null>(null);
  const base = serverTheirs ?? query.data ?? null;
  const initial = { bands: base ? bandDraft(base) : [], ratified: base?.ratified ?? false };
  const keep = useTabDraft<typeof initial, PendingSettingsSave>({
    box: "grade-scale", record: "institution",
    version: base ? String(base.grade_scale_version) : null, initial,
  });
  const { bands, ratified } = keep.value;
  const setBands = (next: Band[] | ((was: Band[]) => Band[])) => keep.set((was) => ({
    ...was, bands: typeof next === "function" ? next(was.bands) : next,
  }));
  const setRatified = (next: boolean) => keep.set((was) => ({ ...was, ratified: next }));
  const refresh = async () => { await query.refetch(); };
  const outcome = useSettingsCommit({ keep, refresh, versionField: "grade_scale_version" });
  const theirs = keep.pending ? null : serverTheirs ?? (keep.conflict ? query.data ?? null : null);
  const edited = base !== null && (ratified !== base.ratified || JSON.stringify(bands) !== JSON.stringify(bandDraft(base)));
  const issues = bandIssues(bands);
  const change = (run: () => void) => {
    outcome.reset();
    run();
  };
  const conflict: Conflict | undefined = theirs
    ? {
        sentence: words.someoneSavedANewerGradeScaleYourEditsAreKeptHere,
        facts: [
          {
            label: words.standing,
            theirs: theirs.ratified ? words.ratified : words.notYetRatified,
          },
          ...theirs.bands.map((band) => ({
            label: settingsText.bandLabel(band.letter),
            theirs: settingsText.bandFacts(band.min_score, band.gpa_points),
          })),
        ],
        keepMine: {
          label: words.keepMine,
          run: () => {
            keep.keepMine();
            setTheirs(null);
            outcome.reset();
          },
        },
        loadNewer: {
          label: words.loadTheirs,
          run: () => {
            keep.loadNewer();
            setTheirs(null);
            outcome.reset();
          },
        },
      }
    : undefined;
  const blockers = (): Blocker[] => [
    ...(theirs
      ? [
          {
            kind: "conflict" as const,
            part: "bands",
            sentence: words.chooseWhichGradeScaleToKeep,
          },
        ]
      : []),
    ...(issues.length
      ? [
          {
            kind: "missing" as const,
            part: "bands",
            sentence: words.correctTheMarkedFieldsBeforeSaving,
          },
        ]
      : []),
  ];
  const box: TaskBox<unknown> = {
    id: "grade-scale",
    kind: "task",
    job: words.maintainTheInstitutionGradeScale,
    label: copy.scale,
    owner: imam,
    read: keep.settled ? boxRead(query, copy.scaleUnavailable) : { status: "loading" },
    fold: "never",
    draft: keep.kept,
    result: theirs ? { state: "none" } : outcome.result,
    detail: () =>
      words.theLetterToGPAScaleEverySectionSGradebookReadsForTheWholeInstitution,
    state: () => ({
      class:
        outcome.result.state === "unknown" ? "reading" : issues.length || theirs
          ? "failed"
          : edited
            ? "draft"
            : ratified
              ? "done"
              : "waiting",
      word: outcome.result.state === "unknown" ? words.notConfirmed : issues.length
        ? settingsText.fieldsToFix(issues.length)
        : theirs
          ? words.aNewerVersionIsWaiting
          : edited
            ? words.changedNotSaved
            : words.scaleRecorded,
    }),
    candidate: { eligible: true, blocked: null, rank: null },
    parts: [
      {
        id: "standing",
        label: words.standing,
        owner: imam,
        fold: "never",
        draft: "browser",
        keep,
        state: { class: "quiet", word: words.institutionDecision },
        markedByBox: true,
        render: () => (
          <div className="flex items-center gap-3">
            <Switch
              disabled={outcome.pending || outcome.result.state === "unknown"}
              id="ratified"
              checked={ratified}
              onCheckedChange={(checked) => change(() => setRatified(checked))}
            />
            <Label htmlFor="ratified">{words.ratified}</Label>
          </div>
        ),
      },
      {
        id: "bands",
        label: words.gradeBands,
        owner: imam,
        fold: "never",
        draft: "browser",
        keep,
        state: { class: "quiet", word: words.minimumScoreAndGPAPoints },
        markedByBox: true,
        conflict,
        render: () => (
          <div
            role="group"
            aria-label={words.gradeBands}
            className="grid gap-3"
          >
            {bands.map((band, index) => (
              <div key={index} className="grid grid-cols-3 gap-2">
                {(["letter", "min_score", "gpa_points"] as const).map(
                  (field) => {
                    const label = {
                      letter: words.letter,
                      min_score: words.minimumScore,
                      gpa_points: words.gPAPoints,
                    }[field];
                    const issue = issues.find(
                      (item) => item.index === index && item.field === field,
                    );
                    const id = `grade-${index}-${field}`;
                    return (
                      <div key={field} className="min-w-0">
                        <Label
                          className={index ? "sr-only" : undefined}
                          htmlFor={id}
                        >
                          {label}
                        </Label>
                        <Input
                          disabled={
                            outcome.pending ||
                            outcome.result.state === "unknown"
                          }
                          id={id}
                          aria-label={`${label}, ${base?.bands[index]?.letter ?? index + 1}`}
                          value={band[field]}
                          inputMode={field === "letter" ? "text" : "decimal"}
                          aria-invalid={Boolean(issue)}
                          aria-describedby={issue ? `${id}-reason` : undefined}
                          onChange={(event) =>
                            change(() =>
                              setBands((previous) =>
                                previous.map((value, i) =>
                                  i === index
                                    ? { ...value, [field]: event.target.value }
                                    : value,
                                ),
                              ),
                            )
                          }
                        />
                        {issue && (
                          <p
                            id={`${id}-reason`}
                            className="text-meta text-destructive"
                          >
                            {issue.sentence}
                          </p>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            ))}
          </div>
        ),
      },
    ],
    commit: {
      verb: words.saveScale,
      cap: words.whenYouSaveTheGradeScale,
      blockers,
      revises: { part: "bands", unchanged: () => !edited },
      effects: () => [
        {
          id: "scale",
          sentence: ratified
            ? words.everySectionUsesThisRatifiedScale
            : words.everySectionUsesThisProvisionalScaleLettersStayAdvisory,
        },
      ],
      run: (key) =>
        outcome.run(
          key,
          async (id) => {
            try {
              const next = await gradeScaleApi.putGradeScale(
                {
                  expected_version: keep.began === null ? base!.grade_scale_version : Number(keep.began),
                  ratified,
                  bands: bands.map((band) => ({
                    letter: band.letter.trim(),
                    min_score: Number(band.min_score),
                    gpa_points: Number(band.gpa_points),
                  })),
                },
                id,
              );
              client.setQueryData(["grade-scale"], next);
              setTheirs(null);
              return next.committed_version === undefined ? undefined : String(next.committed_version);
            } catch (error) {
              if (currentOnConflict(error)) {
                const fresh = await query.refetch();
                if (fresh.data) setTheirs(fresh.data);
              }
              throw error;
            }
          },
          [words.gradeScaleSaved],
          refresh,
        ),
    },
  };
  return box;
}
